// LearnCore Frontend - 阅读材料列表页面

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import type { ReadingPassage, PassageProcessingStatus } from '../types';
import {
  PlusIcon,
  BookOpenIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function ReadingPassagesPage() {
  const [searchParams] = useSearchParams();
  const [passages, setPassages] = useState<ReadingPassage[]>([]);
  const [filteredPassages, setFilteredPassages] = useState<ReadingPassage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'public' | 'private'>('all');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 初始化时显示URL参数中的消息
  useEffect(() => {
    const message = searchParams.get('message');
    if (message) {
      setSuccessMessage(message);
      // 3秒后自动清除消息
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [searchParams]);

  useEffect(() => {
    loadPassages();
  }, []);

  // 轮询机制：如果有处理中的材料，每10秒刷新一次
  useEffect(() => {
    const hasProcessing = passages.some(p => 
      p.processing_status === 'pending' || p.processing_status === 'processing'
    );

    if (hasProcessing) {
      const interval = setInterval(() => {
        loadPassages();
      }, 10000); // 每10秒刷新一次

      return () => clearInterval(interval);
    }
  }, [passages]);

  useEffect(() => {
    // 根据搜索查询和筛选类型过滤阅读材料
    let filtered = passages;

    if (filterType === 'public') {
      filtered = filtered.filter(p => p.is_public === 1);
    } else if (filterType === 'private') {
      filtered = filtered.filter(p => p.is_public === 0);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.original_text.toLowerCase().includes(query)
      );
    }

    setFilteredPassages(filtered);
  }, [searchQuery, filterType, passages]);

  const loadPassages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getReadingPassages();
      setPassages(response.passages);
    } catch (err: any) {
      setError(err.response?.data?.error || '获取阅读材料失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (passageId: string) => {
    if (!confirm('确定要删除这个阅读材料吗？此操作不可撤销。')) {
      return;
    }

    try {
      setDeletingId(passageId);
      await apiClient.deleteReadingPassage(passageId);
      setPassages(prev => prev.filter(p => p.id !== passageId));
    } catch (err: any) {
      alert(err.response?.data?.error || '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRetry = async (passageId: string) => {
    try {
      setRetryingId(passageId);
      setError(null);
      await apiClient.triggerPassageExtraction(passageId);
      
      // 更新本地状态为pending
      setPassages(prev => prev.map(p => 
        p.id === passageId 
          ? { ...p, processing_status: 'pending' as PassageProcessingStatus, processing_error: null }
          : p
      ));
      
      setSuccessMessage('已重新触发内容提取，请稍后查看结果');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      alert(err.response?.data?.error || '重试失败');
    } finally {
      setRetryingId(null);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPreviewText = (text: string) => {
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  // 获取状态显示组件
  const getStatusBadge = (status: PassageProcessingStatus) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            已完成
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            部分完成
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-3 w-3 mr-1 animate-spin" />
            处理中
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            等待处理
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            提取失败
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <DocumentTextIcon className="h-3 w-3 mr-1" />
            草稿
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载阅读材料..." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <DocumentTextIcon className="h-8 w-8 mr-3 text-primary-600" />
              我的阅读材料
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              创建、管理和学习你的英语阅读材料
            </p>
          </div>

          <Link
            to="/passages/create"
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            创建阅读材料
          </Link>
        </div>
      </div>

      {/* 成功消息 */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3" />
            <p className="text-sm text-green-800">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* 搜索和筛选 */}
      <div className="mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="搜索标题或内容..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}
            >
              全部
            </button>
            <button
              onClick={() => setFilterType('public')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'public'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}
            >
              公开
            </button>
            <button
              onClick={() => setFilterType('private')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === 'private'
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}
            >
              私有
            </button>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                加载失败
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error}
              </div>
              <div className="mt-4">
                <button
                  onClick={loadPassages}
                  className="btn-outline text-sm"
                >
                  重试
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 阅读材料列表 */}
      {filteredPassages.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery || filterType !== 'all' ? '没有找到匹配的阅读材料' : '还没有阅读材料'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || filterType !== 'all'
              ? '尝试调整搜索条件或筛选类型。'
              : '创建你的第一个英语阅读材料，开始深度学习之旅。'
            }
          </p>
          {(!searchQuery && filterType === 'all') && (
            <Link to="/passages/create" className="btn-primary">
              <PlusIcon className="h-4 w-4 mr-2 inline" />
              创建阅读材料
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPassages.map((passage) => (
            <div
              key={passage.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
            >
              <div className="p-5 flex-1 flex flex-col">
                {/* 标题和隐私状态 */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 flex-1">
                    {passage.title}
                  </h3>
                  <div className="ml-2 flex-shrink-0">
                    {passage.is_public ? (
                      <ShareIcon className="h-5 w-5 text-green-500" title="公开" />
                    ) : (
                      <BookOpenIcon className="h-5 w-5 text-gray-400" title="私有" />
                    )}
                  </div>
                </div>

                {/* 处理状态 */}
                <div className="mb-3">
                  {getStatusBadge(passage.processing_status)}
                </div>

                {/* 失败或部分完成错误信息 */}
                {(passage.processing_status === 'failed' || passage.processing_status === 'partial') && passage.processing_error && (
                  <div className={`mb-3 p-2 border rounded text-xs ${
                    passage.processing_status === 'partial'
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                    {passage.processing_error}
                  </div>
                )}

                {/* 内容预览 */}
                <div className="mb-3 min-h-[3rem]">
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {getPreviewText(passage.original_text)}
                  </p>
                </div>

                {/* 元信息 */}
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span>{formatDate(passage.updated_at)}</span>
                </div>

                {/* 操作按钮 */}
                <div className="flex flex-wrap gap-2 mt-auto">
                  {/* 失败或部分完成状态显示重试按钮 */}
                  {(passage.processing_status === 'failed' || passage.processing_status === 'partial') && (
                    <button
                      onClick={() => handleRetry(passage.id)}
                      disabled={retryingId === passage.id}
                      className="flex-1 btn-primary text-center text-sm py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                    >
                      {retryingId === passage.id ? (
                        <>
                          <div className="h-4 w-4 mr-1 inline-block animate-spin rounded-full border-2 border-white border-t-transparent" />
                          重试中...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-1 inline" />
                          {passage.processing_status === 'partial' ? '重试失败段落' : '重新提取'}
                        </>
                      )}
                    </button>
                  )}

                  {/* 完成或部分完成状态显示学习按钮 */}
                  {(passage.processing_status === 'completed' || passage.processing_status === 'partial') && (
                    <Link
                      to={`/passages/${passage.id}/study`}
                      className="flex-1 btn-primary text-center text-sm py-2"
                    >
                      <BookOpenIcon className="h-4 w-4 mr-1 inline" />
                      学习
                    </Link>
                  )}

                  {/* 处理中或等待状态显示禁用按钮 */}
                  {(passage.processing_status === 'processing' || passage.processing_status === 'pending') && (
                    <button
                      disabled
                      className="flex-1 btn-primary text-center text-sm py-2 opacity-50 cursor-not-allowed"
                    >
                      <ClockIcon className="h-4 w-4 mr-1 inline animate-spin" />
                      处理中...
                    </button>
                  )}

                  {/* 草稿状态显示编辑按钮 */}
                  {passage.processing_status === 'draft' && (
                    <Link
                      to={`/passages/${passage.id}/edit`}
                      className="flex-1 btn-primary text-center text-sm py-2"
                    >
                      <PencilIcon className="h-4 w-4 mr-1 inline" />
                      继续编辑
                    </Link>
                  )}

                  <Link
                    to={`/passages/${passage.id}/edit`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="编辑"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Link>

                  <Link
                    to={`/passages/${passage.id}/view`}
                    className="btn-outline px-3 py-2 text-sm"
                    title="查看详情"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Link>

                  <button
                    onClick={() => handleDelete(passage.id)}
                    disabled={deletingId === passage.id}
                    className="btn-outline px-3 py-2 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                    title="删除"
                  >
                    {deletingId === passage.id ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 底部提示 */}
      {filteredPassages.length > 0 && (
        <div className="mt-12 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              💡 使用说明
            </h3>
            <div className="text-sm text-blue-700 space-y-2 text-left">
              <p>• <strong>处理中的材料：</strong>系统正在后台分析，页面会每10秒自动刷新状态</p>
              <p>• <strong>部分完成的材料：</strong>部分段落提取成功，可以先学习，点击"重试失败段落"补全剩余内容</p>
              <p>• <strong>失败的材料：</strong>点击"重新提取"按钮可以重试</p>
              <p>• <strong>草稿材料：</strong>点击"继续编辑"可以手动输入内容或触发智能提取</p>
              <p>• <strong>完成的材料：</strong>点击"学习"开始使用提取的内容学习</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
